import { getTrackerField } from '@nobia/zeus-components/lib/analytics'
import {
  authRefreshSuccess,
  authRequestError,
} from '@nobia/zeus-components/lib/auth/authActions'
import { calculateExpiration } from '@nobia/zeus-components/lib/auth/helpers'
import queryString from '@nobia/zeus-components/lib/helpers/queryString'
import settings from '@nobia/zeus-components/lib/settings'

export const AUTH_TIMEOUT_STATUS = 419
const REFRESH_PATH = '/login/refresh'

let refreshPromise = null
const refreshSuccessListeners = new Set()

function getSharedStore() {
  if (typeof window === 'undefined') {
    return null
  }
  return window.nobia?.sharedStore ?? null
}

function getSessionFromStore() {
  const store = getSharedStore()
  if (!store) {
    return null
  }
  return store.getState()?.auth?.session ?? null
}

export function getRefreshCredentials() {
  const session = getSessionFromStore()
  if (session?.refreshToken && session?.provider) {
    return {
      refreshToken: session.refreshToken,
      provider: session.provider,
    }
  }

  const omni = typeof window !== 'undefined' ? window.__OMNI__ : null
  if (omni?.refresh_token && omni?.provider) {
    return {
      refreshToken: omni.refresh_token,
      provider: omni.provider,
    }
  }

  return null
}

export function buildLoginPath() {
  const query = queryString.stringify({
    ...queryString.parse(getTrackerField('linkerParam')),
    ReturnUrl: window.location.pathname,
    forced: true,
    ...(settings.login.query || {}),
  })
  return `${settings.login.path}?${query}`
}

export function redirectToLogin() {
  const store = getSharedStore()
  if (store) {
    store.dispatch(authRequestError({}))
  }
  window.location.href = buildLoginPath()
}

export function applyRefreshPayload(json) {
  const payload = {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    idToken: json.id_token,
    expiresIn: json.expires_in,
    expiresAt: calculateExpiration({ expiresIn: json.expires_in }),
  }

  if (typeof window !== 'undefined' && window.__OMNI__) {
    window.__OMNI__.access_token = json.access_token
    if (json.refresh_token) {
      window.__OMNI__.refresh_token = json.refresh_token
    }
    if (json.id_token) {
      window.__OMNI__.id_token = json.id_token
    }
    if (payload.expiresAt != null) {
      window.__OMNI__.expires_at = Math.floor(payload.expiresAt / 1000)
    }
  }

  const store = getSharedStore()
  if (store) {
    store.dispatch(authRefreshSuccess(payload))
  }

  refreshSuccessListeners.forEach((listener) => {
    try {
      listener()
    } catch (error) {
      console.error(error)
    }
  })

  return json.access_token
}

async function postRefresh() {
  const credentials = getRefreshCredentials()
  if (!credentials) {
    redirectToLogin()
    throw new Error('missing refresh credentials')
  }

  const response = await fetch(REFRESH_PATH, {
    method: 'POST',
    body: JSON.stringify(credentials),
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    redirectToLogin()
    throw new Error('refresh failed')
  }

  const json = await response.json()
  return applyRefreshPayload(json)
}

export function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = postRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

export function onAuthRefreshSuccess(listener) {
  refreshSuccessListeners.add(listener)
  return () => {
    refreshSuccessListeners.delete(listener)
  }
}

export function isRefreshRequest(input) {
  const url = typeof input === 'string' ? input : input?.url
  return Boolean(url && url.includes(REFRESH_PATH))
}

export function isAuthTimeoutStatus(status) {
  return status === AUTH_TIMEOUT_STATUS
}

function getErrorStatus(error) {
  if (!error) {
    return undefined
  }
  return (
    error.statusCode ??
    error.status ??
    error.cause?.statusCode ??
    error.cause?.status
  )
}

export function isAuthTimeoutError(error) {
  return isAuthTimeoutStatus(getErrorStatus(error))
}

export function isNotificationRequest(input) {
  const url = typeof input === 'string' ? input : input?.url
  return Boolean(url && url.includes('/notification/'))
}

function retryInitWithFreshToken(init) {
  const token =
    typeof window !== 'undefined' ? window.__OMNI__?.access_token : null
  if (!token) {
    return init
  }

  const existingHeaders = init?.headers || {}
  if (typeof Headers !== 'undefined' && existingHeaders instanceof Headers) {
    const headers = new Headers(existingHeaders)
    if (headers.has('authorization')) {
      headers.set('authorization', `Bearer ${token}`)
    }
    return {
      ...init,
      headers,
    }
  }

  const headers = { ...existingHeaders }
  const authKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === 'authorization'
  )
  if (authKey) {
    headers[authKey] = `Bearer ${token}`
  }

  return {
    ...init,
    headers,
  }
}

export function installAuth419FetchInterceptor() {
  if (typeof window === 'undefined' || window.__auth419FetchInstalled) {
    return
  }

  const nativeFetch = window.fetch.bind(window)

  window.fetch = async function auth419Fetch(input, init) {
    if (isRefreshRequest(input) || isNotificationRequest(input)) {
      return nativeFetch(input, init)
    }

    const response = await nativeFetch(input, init)
    if (!isAuthTimeoutStatus(response.status)) {
      return response
    }

    try {
      await refreshAccessToken()
    } catch {
      return response
    }

    return nativeFetch(input, retryInitWithFreshToken(init))
  }

  window.__auth419FetchInstalled = true
}

export async function refreshIfSessionExpired() {
  const session = getSessionFromStore()
  const omni = typeof window !== 'undefined' ? window.__OMNI__ : null
  const expiresAt =
    session?.expiresAt ??
    (omni?.expires_at ? parseInt(omni.expires_at, 10) * 1000 : null)

  if (expiresAt != null && Date.now() >= expiresAt) {
    return refreshAccessToken()
  }

  return null
}

export function installAuth419VisibilityRefresh() {
  if (typeof document === 'undefined' || window.__auth419VisibilityInstalled) {
    return
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      return
    }
    refreshIfSessionExpired().catch(() => {})
  })

  window.__auth419VisibilityInstalled = true
}
